export const DEFAULT_VOTE_FILTERS = {
  search: "",
  type: "all", // all | vote | sondage
  status: "available", // all | available | answered | upcoming | closed
};

export function getStoredUserId() {
  try {
    const raw = localStorage.getItem("userInfo");
    const userInfo = raw ? JSON.parse(raw) : null;

    return (
      userInfo?.userId ||
      userInfo?.id ||
      userInfo?.user_id ||
      userInfo?.User?.id ||
      null
    );
  } catch {
    return null;
  }
}

export function getStoredToken() {
  try {
    return localStorage.getItem("token") || "";
  } catch {
    return "";
  }
}

export function formatVoteDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date
    .toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    .replace(",", " -");
}

export function formatVoteDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
}

export function stripHtml(value = "") {
  const text = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

  return text;
}

export function getVoteTypeMeta(type) {
  const normalized = String(type || "vote").toLowerCase();

  if (normalized === "sondage") {
    return {
      key: "sondage",
      label: "Sondage",
      className:
        "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60",
    };
  }

  return {
    key: "vote",
    label: "Vote",
    className:
      "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-900/20 dark:text-sky-200 dark:ring-sky-800/60",
  };
}

export function getVoteStatus(vote) {
  const now = new Date();

  const start = vote?.start_time ? new Date(vote.start_time) : null;
  const end = vote?.end_time ? new Date(vote.end_time) : null;

  if (!vote?.is_active) {
    return {
      key: "inactive",
      label: "Inactif",
      canParticipate: false,
      className:
        "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60",
    };
  }

  if (start && !Number.isNaN(start.getTime()) && now < start) {
    return {
      key: "upcoming",
      label: "Programmé",
      canParticipate: false,
      className:
        "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-800/60",
    };
  }

  if (end && !Number.isNaN(end.getTime()) && now > end) {
    return {
      key: "closed",
      label: "Clôturé",
      canParticipate: false,
      className:
        "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700",
    };
  }

  return {
    key: "available",
    label: "Ouvert",
    canParticipate: true,
    className:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60",
  };
}

export function getBallotForVote(ballots = [], voteId) {
  return (
    (ballots || []).find(
      (ballot) => Number(ballot?.vote_id) === Number(voteId)
    ) || null
  );
}

export function filterVotes(votes = [], ballots = [], filters = DEFAULT_VOTE_FILTERS) {
  const search = String(filters.search || "").trim().toLowerCase();

  return (votes || []).filter((vote) => {
    const ballot = getBallotForVote(ballots, vote?.id);
    const status = getVoteStatus(vote);
    const typeMeta = getVoteTypeMeta(vote?.type);

    const matchesSearch =
      !search ||
      [
        vote?.title,
        vote?.description,
        vote?.type,
        status.label,
        typeMeta.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search);

    const matchesType =
      filters.type === "all" || typeMeta.key === filters.type;

    const matchesStatus =
      filters.status === "all" ||
      (filters.status === "answered" && ballot) ||
      (filters.status === "available" && status.key === "available" && !ballot) ||
      (filters.status === "upcoming" && status.key === "upcoming") ||
      (filters.status === "closed" && status.key === "closed");

    return matchesSearch && matchesType && matchesStatus;
  });
}

export function sortVotesForUser(votes = [], ballots = []) {
  return (votes || []).slice().sort((a, b) => {
    const aBallot = getBallotForVote(ballots, a?.id);
    const bBallot = getBallotForVote(ballots, b?.id);

    if (!!aBallot !== !!bBallot) {
      return aBallot ? 1 : -1;
    }

    const aStatus = getVoteStatus(a);
    const bStatus = getVoteStatus(b);

    const statusOrder = {
      available: 1,
      upcoming: 2,
      closed: 3,
      inactive: 4,
    };

    const statusDiff =
      (statusOrder[aStatus.key] || 9) - (statusOrder[bStatus.key] || 9);

    if (statusDiff !== 0) return statusDiff;

    const aStart = a?.start_time ? new Date(a.start_time).getTime() : 0;
    const bStart = b?.start_time ? new Date(b.start_time).getTime() : 0;

    return bStart - aStart;
  });
}

export function normalizeQuestionType(type) {
  const value = String(type || "").toLowerCase();

  if (value === "open") return "open";
  if (value === "multiple") return "multiple";

  return "single";
}

export function isQuestionRequired(question) {
  if (question?.required === false) return false;
  if (question?.is_required === false) return false;

  return true;
}

export function validateVoteResponses(vote, responses = {}) {
  const questions = Array.isArray(vote?.questions) ? vote.questions : [];

  const missing = questions.filter((question) => {
    if (!isQuestionRequired(question)) return false;

    const type = normalizeQuestionType(question?.type);
    const value = responses[question.id];

    if (type === "open") {
      return !String(value || "").trim();
    }

    if (type === "multiple") {
      return !Array.isArray(value) || value.length === 0;
    }

    return value === undefined || value === null || value === "";
  });

  return {
    valid: missing.length === 0,
    missing,
  };
}

export function getQuestionTypeLabel(type) {
  const normalized = normalizeQuestionType(type);

  if (normalized === "open") return "Réponse libre";
  if (normalized === "multiple") return "Choix multiples";

  return "Choix unique";
}